/**
 * ExecutionModeSelect
 * Chat model-mode picker: popover anchored to the trigger on desktop,
 * full-width bottom sheet (height follows content) on mobile.
 */

import React, { useState } from 'react';
import { Drawer, Popover, useMediaQuery, useTheme } from '@mui/material';
import { Check, ChevronDown } from 'lucide-react';
import { CHAT_EXECUTION_MODE_OPTIONS } from '../../common/chatExecutionMode';
import type { ChatExecutionMode } from '../../hooks/useChatAgent';

interface ExecutionModeSelectProps {
  value: ChatExecutionMode;
  onChange: (mode: ChatExecutionMode) => void;
  disabled?: boolean;
}

export const ExecutionModeSelect: React.FC<ExecutionModeSelectProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const selected = CHAT_EXECUTION_MODE_OPTIONS.find((option) => option.value === value);

  const close = () => setAnchorEl(null);
  const select = (mode: ChatExecutionMode) => {
    onChange(mode);
    close();
  };

  const options = (
    <ul role="listbox" aria-label="Model mode" className="min-w-[140px] p-1.5">
      {CHAT_EXECUTION_MODE_OPTIONS.map((option) => {
        const isSelected = option.value === value;
        return (
          <li key={option.value} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              onClick={() => select(option.value)}
              className={`flex w-full items-center justify-between gap-6 rounded-lg px-3 py-2.5 text-left text-sm font-medium capitalize transition-colors ${
                isSelected
                  ? 'bg-blue-50 text-blue-600 dark:bg-slate-700/70 dark:text-blue-300'
                  : 'text-gray-700 hover:bg-gray-100 dark:text-slate-200 dark:hover:bg-slate-700/50'
              }`}
            >
              <span>{option.label}</span>
              {isSelected && <Check className="h-4 w-4" />}
            </button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <button
        type="button"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        disabled={disabled}
        aria-label="Model mode"
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2 text-xs font-medium capitalize text-gray-700 outline-none transition-colors hover:border-gray-300 focus:border-blue-400 disabled:cursor-wait disabled:opacity-60 dark:border-slate-700 dark:bg-[#0F1F38] dark:text-slate-300 dark:focus:border-slate-500"
      >
        <span>{selected?.label ?? value}</span>
        <ChevronDown className="h-3.5 w-3.5" />
      </button>

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          PaperProps={{
            className:
              'rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)] dark:bg-slate-800 dark:text-slate-100',
          }}
        >
          <div className="px-2 pb-2 pt-3">
            <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-gray-300 dark:bg-slate-600" />
            {options}
          </div>
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={close}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          slotProps={{
            paper: {
              className:
                'mb-2 rounded-xl border border-gray-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800',
            },
          }}
        >
          {options}
        </Popover>
      )}
    </>
  );
};
